import { useEffect, useRef, useState } from "react";
import { VESSEL_CARD_EVALUATION_MAX_CHARACTERS, VESSEL_CARD_EVALUATION_MAX_WORDS } from "../constants/vessel-card-evaluation.constants";
import { VesselCardEvaluationReason, VesselCardEvaluationRequest } from "../models/vessel-card-evaluation.models";
import { VesselCardVesselOption } from "../models/vessel-card.models";
import { getVesselCardMockEvaluationRequests } from "../mocks/vessel-card.mocks";
import {
  getVesselCardEvaluationDownload,
  getVesselCardEvaluationErrorMessage,
  getVesselCardEvaluationHistory,
  submitVesselCardEvaluation,
} from "../services/vessel-card-evaluation.service";
import {
  countVesselCardEvaluationWords,
  getVesselCardEvaluationNotificationLabel,
  validateVesselCardEvaluationFiles,
} from "../utils/vessel-card-evaluation.utils";

// Owns a single dialog draft and retrieves its vessel's saved requests.
// Assumes a reason alone is a valid request; supporting files and additional details are optional.
// Keeps one submission reference across retries, changing it only when the draft changes, to avoid duplicate saved requests.
// Mock mode shows sample attachment metadata and validates a preview without submitting or accessing live files.
export const useVesselCardEvaluation = (
  vessel: VesselCardVesselOption,
  useMockData: boolean,
  onSubmittingChange: (submitting: boolean) => void,
  canManageRequests: boolean,
) => {
  const [reason, setReason] = useState<VesselCardEvaluationReason | "">("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionNotice, setSubmissionNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requests, setRequests] = useState<VesselCardEvaluationRequest[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyRevision, setHistoryRevision] = useState(0);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const submissionId = useRef<string | null>(null);
  const submissionController = useRef<AbortController | null>(null);
  const downloadController = useRef<AbortController | null>(null);
  const wordCount = countVesselCardEvaluationWords(additionalDetails);
  const wordLimitExceeded = wordCount > VESSEL_CARD_EVALUATION_MAX_WORDS;
  const characterLimitExceeded = additionalDetails.length > VESSEL_CARD_EVALUATION_MAX_CHARACTERS;

  useEffect(() => () => {
    submissionController.current?.abort();
    downloadController.current?.abort();
    onSubmittingChange(false);
  }, [onSubmittingChange]);

  // Only history is refreshed after saving; a failed history fetch never turns a successful submission into a failed request.
  useEffect(() => {

    if (useMockData) {
      setRequests(getVesselCardMockEvaluationRequests({ imo: vessel.imo, vesselName: vessel.vesselName }));

      return;
    }

    if (!canManageRequests) {
      return;
    }

    const controller = new AbortController();
    setIsHistoryLoading(true);
    setHistoryError(null);
    getVesselCardEvaluationHistory(vessel.imo, controller.signal)
      .then((response) => {

        if (!controller.signal.aborted) {
          setRequests(response.requests);
        }
      })
      .catch((error: unknown) => {

        if (!controller.signal.aborted) {
          setHistoryError(getVesselCardEvaluationErrorMessage(error, "Unable to load evaluation requests. Try refreshing the list."));
        }
      })
      .finally(() => {

        if (!controller.signal.aborted) {
          setIsHistoryLoading(false);
        }
      });

    return () => controller.abort();
  }, [vessel.imo, vessel.vesselName, useMockData, historyRevision, canManageRequests]);

  // === EVALUATION DRAFT ===
  const markDraftChanged = () => {
    submissionId.current = null;
    setSubmissionError(null);
    setSubmissionNotice(null);
  };

  const changeReason = (value: VesselCardEvaluationReason | "") => {

    if (submissionController.current || value === reason || (!useMockData && !canManageRequests)) {
      return;
    }

    markDraftChanged();
    setReason(value);
  };

  const changeAdditionalDetails = (value: string) => {

    if (submissionController.current || value === additionalDetails) {
      return;
    }

    markDraftChanged();
    setAdditionalDetails(value);
  };

  const addFiles = (selection: File[]) => {

    if (submissionController.current || !selection.length) {
      return;
    }

    const combined = [...files];
    selection.forEach((file) => {
      const alreadySelected = combined.some((existing) => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified);

      if (!alreadySelected) {
        combined.push(file);
      }
    });
    const error = validateVesselCardEvaluationFiles(combined);
    setFileError(error);

    if (error || combined.length === files.length) {
      return;
    }

    markDraftChanged();
    setFiles(combined);
  };

  const removeFile = (index: number) => {

    if (submissionController.current || !files[index]) {
      return;
    }

    markDraftChanged();
    setFileError(null);
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  };

  // === EVALUATION SUBMISSION ===
  const submitRequest = async () => {

    if (submissionController.current || (!useMockData && !canManageRequests)) {
      return;
    }

    setSubmissionError(null);

    if (!reason || wordLimitExceeded || characterLimitExceeded) {
      setSubmissionError(!reason ? "Select a reason for the evaluation." : "Additional details must contain no more than 2,000 words and 100,000 characters.");

      return;
    }

    const invalidFiles = validateVesselCardEvaluationFiles(files);

    if (invalidFiles) {
      setFileError(invalidFiles);

      return;
    }

    if (useMockData) {
      setSubmissionNotice("Preview validated. No evaluation request was saved, no files were uploaded, and DVP was not notified.");

      return;
    }

    const controller = new AbortController();
    submissionController.current = controller;
    setSubmissionNotice(null);
    setIsSubmitting(true);
    onSubmittingChange(true);

    try {
      submissionId.current ??= window.crypto.randomUUID();
      const response = await submitVesselCardEvaluation({
        imo: vessel.imo,
        reason,
        additionalDetails,
        submissionId: submissionId.current,
      }, files, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setSubmissionNotice(`Request ${response.requestId}. ${getVesselCardEvaluationNotificationLabel(response.notificationStatus)}`);
      setReason("");
      setAdditionalDetails("");
      setFiles([]);
      setFileError(null);
      submissionId.current = null;
      setHistoryRevision((current) => current + 1);
    } catch (error: unknown) {

      if (!controller.signal.aborted) {
        setSubmissionError(getVesselCardEvaluationErrorMessage(error, "Unable to confirm that your request was saved. Retry this unchanged draft to avoid creating a duplicate."));
      }
    } finally {

      if (!controller.signal.aborted) {
        submissionController.current = null;
        setIsSubmitting(false);
        onSubmittingChange(false);
      }
    }
  };

  // === EVALUATION ATTACHMENTS ===
  const downloadAttachment = async (requestId: string, attachmentId: string) => {

    if (useMockData || !canManageRequests || downloadController.current) {
      return;
    }

    const controller = new AbortController();
    downloadController.current = controller;
    setDownloadingAttachmentId(attachmentId);
    setDownloadError(null);

    try {
      const response = await getVesselCardEvaluationDownload(requestId, attachmentId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      const link = document.createElement("a");
      link.href = response.url;
      link.download = response.fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: unknown) {

      if (!controller.signal.aborted) {
        setDownloadError(getVesselCardEvaluationErrorMessage(error, "Unable to download the attachment. Try again."));
      }
    } finally {

      if (!controller.signal.aborted) {
        downloadController.current = null;
        setDownloadingAttachmentId(null);
      }
    }
  };

  return {
    reason, additionalDetails, files, fileError, submissionError, submissionNotice, isSubmitting,
    requests, isHistoryLoading, historyError, downloadingAttachmentId, downloadError, wordCount, wordLimitExceeded, characterLimitExceeded,
    changeReason, changeAdditionalDetails, addFiles, removeFile, submitRequest, downloadAttachment,
    refreshHistory: () => setHistoryRevision((current) => current + 1),
  };
};
